(function () {
  class VeraniCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.vcd__panel');
      this.body = this.querySelector('[data-vcd-body]');
      this.footer = this.querySelector('[data-vcd-footer]');
      this.subtotal = this.querySelector('[data-vcd-subtotal]');
      this.titleCount = this.querySelector('[data-vcd-title-count]');
      this.reward = this.querySelector('[data-vcd-reward]');
      this.rewardText = this.querySelector('[data-vcd-reward-text]');
      this.rewardPercent = this.querySelector('[data-vcd-reward-percent]');
      this.rewardBar = this.querySelector('[data-vcd-reward-bar]');
      this.priorityTemplate = this.querySelector('[data-vcd-priority-template]');
      this.upsellTemplate = this.querySelector('[data-vcd-upsell-template]');
      this.emptyRecommendations = this.querySelector('[data-vcd-empty-recommendations]');
      this.quantityTimers = new Map();
      this.lastFocused = null;
      this.syncingGift = false;
      this.dragState = { active: false, rail: null, startX: 0, startScroll: 0, moved: false };

      this.addEventListener('click', this.onClick.bind(this));
      // Drag-to-scroll is mouse-only on purpose: the rail already has native
      // overflow-x touch scrolling, and layering synthetic scrollLeft writes
      // on top of it via touch listeners fights the browser's own momentum
      // scroll and causes jitter on mobile.
      this.addEventListener('mousedown', this.onRailPointerDown.bind(this));
      window.addEventListener('mousemove', this.onRailPointerMove.bind(this));
      window.addEventListener('mouseup', this.onRailPointerUp.bind(this));
      document.addEventListener('click', this.onDocumentClick.bind(this));
      document.addEventListener('submit', this.onSubmit.bind(this), true);
      document.addEventListener('keydown', this.onKeydown.bind(this));

      this.refresh(false);
    }

    get openState() {
      return this.getAttribute('aria-hidden') === 'false';
    }

    open() {
      this.lastFocused = document.activeElement;
      this.setAttribute('aria-hidden', 'false');
      this.lockScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = `-${this.lockScrollY}px`;
      document.body.classList.add('vcd-open');
      this.panel && this.panel.focus();
      this.refresh(false);
    }

    close() {
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('vcd-open');
      document.body.style.top = '';
      window.scrollTo(0, this.lockScrollY || 0);
      if (this.lastFocused && typeof this.lastFocused.focus === 'function') this.lastFocused.focus();
    }

    setBusy(state) {
      this.classList.toggle('is-busy', Boolean(state));
    }

    async request(path, options) {
      const response = await fetch(path, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        ...options
      });
      if (!response.ok) throw new Error(`Cart request failed: ${path}`);
      return response.json();
    }

    fetchCart() {
      return this.request('/cart.js');
    }

    async refresh(openAfter) {
      try {
        let cart = await this.fetchCart();
        const giftChanged = await this.syncRewardGift(cart);
        if (giftChanged) cart = await this.fetchCart();
        this.render(cart);
        this.updateExternalCounts(cart.item_count);
        if (openAfter) this.open();
      } catch (error) {
        if (this.body) {
          this.body.innerHTML = '<div class="vcd__empty"><div class="vcd__empty-inner"><h3>Cart unavailable</h3><p>Please refresh and try again.</p></div></div>';
        }
      }
    }

    render(cart) {
      if (!this.body) return;

      if (!cart.items || cart.items.length === 0) {
        this.renderEmpty();
        this.updateReward(cart);
        return;
      }

      const lineItems = cart.items.map((item, index) => this.itemMarkup(item, index + 1)).join('');
      this.body.innerHTML = [
        `<div class="vcd__items">${lineItems}</div>`,
        this.priorityMarkup(cart),
        this.upsellMarkup(cart)
      ].join('');

      if (this.footer) this.footer.hidden = false;
      if (this.subtotal) this.subtotal.textContent = this.formatMoney(cart.total_price, true);
      this.updateTitleCount(cart.item_count);
      this.updateReward(cart);
      this.updatePriorityState(cart);
    }

    renderEmpty() {
      const recs = this.emptyRecommendations ? this.emptyRecommendations.innerHTML.trim() : '';
      this.body.innerHTML = `
        <div class="vcd__empty">
          <div class="vcd__empty-inner">
            <h3>${this.escape(this.dataset.emptyTitle || '')}</h3>
            ${this.dataset.emptyBody ? `<p>${this.escape(this.dataset.emptyBody)}</p>` : ''}
            ${recs ? `<div class="vcd__upsell-rail" data-upsell-rail>${recs}</div>` : ''}
            <a class="vcd__empty-link" href="${this.escape(this.dataset.emptyButtonLink || '/collections/all')}">${this.escape(this.dataset.emptyButtonLabel || '')}</a>
          </div>
        </div>
      `;
      if (this.footer) this.footer.hidden = true;
      this.updateTitleCount(0);
    }

    itemMarkup(item, line) {
      const image = item.image ? `<img src="${this.escape(item.image)}" alt="${this.escape(item.product_title)}" loading="lazy">` : '';
      const title = this.escape(item.product_title || item.title || '');
      const titleHtml = this.dataset.titleLinks === 'true'
        ? `<a class="vcd__item-title" href="${this.escape(item.url || '#')}">${title}</a>`
        : `<span class="vcd__item-title">${title}</span>`;
      const variant = this.variantText(item);
      const compare = item.original_line_price > item.final_line_price
        ? `<s class="vcd__compare">${this.formatMoney(item.original_line_price, true)}</s>`
        : '';
      const lowStock = this.lowStockMarkup(item);

      return `
        <article class="vcd__item" data-line="${line}" data-key="${this.escape(item.key)}" data-variant-id="${item.variant_id}">
          <a class="vcd__item-media" href="${this.escape(item.url || '#')}">${image}</a>
          <div class="vcd__item-info">
            <div class="vcd__item-top">
              <div>
                ${titleHtml}
                ${variant ? `<p class="vcd__item-variant">${this.escape(variant)}</p>` : ''}
                ${lowStock}
              </div>
              <span class="vcd__item-price">${this.formatMoney(item.final_line_price, true)}${compare}</span>
            </div>
            <div class="vcd__item-actions">
              <div class="vcd__qty" aria-label="Quantity">
                <button type="button" data-vcd-qty="-1" aria-label="Decrease quantity">-</button>
                <span data-vcd-quantity>${item.quantity}</span>
                <button type="button" data-vcd-qty="1" aria-label="Increase quantity">+</button>
              </div>
              <button type="button" class="vcd__remove" data-vcd-remove>${this.escape(this.dataset.lineRemoveLabel || 'Remove')}</button>
            </div>
          </div>
        </article>
      `;
    }

    variantText(item) {
      if (item.variant_title && item.variant_title !== 'Default Title') return item.variant_title;
      if (!Array.isArray(item.options_with_values)) return '';
      return item.options_with_values
        .map((option) => option.value)
        .filter(Boolean)
        .join(' / ');
    }

    lowStockMarkup(item) {
      if (this.dataset.lowStockEnabled !== 'true') return '';
      const quantity = item.variant_inventory_quantity;
      const threshold = Number(this.dataset.lowStockThreshold || 0);
      if (!quantity || quantity > threshold) return '';
      const text = (this.dataset.lowStockTemplate || '').replace('[qty]', quantity);
      return text ? `<p class="vcd__low-stock">${this.escape(text)}</p>` : '';
    }

    priorityMarkup(cart) {
      if (this.dataset.priorityEnabled !== 'true' || !this.priorityTemplate) return '';
      const wrapper = document.createElement('div');
      wrapper.innerHTML = this.priorityTemplate.innerHTML;
      return wrapper.innerHTML;
    }

    upsellMarkup(cart) {
      if (this.dataset.upsellEnabled !== 'true' || !this.upsellTemplate) return '';
      const wrapper = document.createElement('div');
      wrapper.innerHTML = this.upsellTemplate.innerHTML;
      const cartProducts = new Set((cart.items || []).map((item) => String(item.product_id)));
      if (this.dataset.upsellHideInCart === 'true') {
        wrapper.querySelectorAll('[data-vcd-upsell]').forEach((card) => {
          if (cartProducts.has(String(card.dataset.productId))) card.remove();
        });
      }
      const section = wrapper.querySelector('[data-vcd-upsells]');
      if (!section || !section.querySelector('[data-vcd-upsell]')) return '';
      return section.outerHTML;
    }

    updateReward(cart) {
      if (!this.reward || this.dataset.rewardEnabled !== 'true') return;
      const threshold = Number(this.dataset.rewardThreshold || 0);
      if (!threshold) {
        this.reward.hidden = true;
        return;
      }

      this.reward.hidden = false;
      const total = Number(cart.total_price || 0);
      const remaining = Math.max(threshold - total, 0);
      const percent = Math.min(Math.round((total / threshold) * 100), 100);
      const text = remaining > 0
        ? (this.dataset.rewardBefore || '').replace('[amount_remaining]', this.formatMoney(remaining, true))
        : (this.dataset.rewardReached || '');

      if (this.rewardText) this.rewardText.textContent = text;
      if (this.rewardBar) this.rewardBar.style.width = `${percent}%`;
      if (this.rewardPercent) {
        this.rewardPercent.hidden = this.dataset.rewardShowPercent !== 'true';
        this.rewardPercent.textContent = `${percent}%`;
      }
    }

    async syncRewardGift(cart) {
      if (this.syncingGift) return false;
      if (this.dataset.rewardGiftAuto !== 'true') return false;
      const threshold = Number(this.dataset.rewardThreshold || 0);
      const giftVariant = Number(this.dataset.rewardGiftVariantId || 0);
      if (!threshold || !giftVariant) return false;

      const giftLine = (cart.items || []).find((item) => Number(item.variant_id) === giftVariant);
      const cartTotalWithoutGift = cart.total_price - (giftLine ? giftLine.final_line_price : 0);
      const shouldHaveGift = cartTotalWithoutGift >= threshold;

      try {
        this.syncingGift = true;
        if (shouldHaveGift && !giftLine) {
          await this.addVariant(giftVariant, 1, { _verani_reward_gift: 'true' }, false);
          return true;
        } else if (!shouldHaveGift && giftLine) {
          await this.changeLineByKey(giftLine.key, 0, false);
          return true;
        }
      } finally {
        this.syncingGift = false;
      }
      return false;
    }

    updatePriorityState(cart) {
      const variantId = Number(this.dataset.priorityVariantId || 0);
      const toggle = this.querySelector('[data-vcd-priority-toggle]');
      if (!toggle || !variantId) return;
      const active = (cart.items || []).some((item) => Number(item.variant_id) === variantId);
      toggle.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    updateTitleCount(count) {
      if (this.titleCount) this.titleCount.textContent = `(${count || 0})`;
    }

    updateExternalCounts(count) {
      document.querySelectorAll('[data-cart-count], .cart-count-bubble span:first-child, cart-bubble .cart-bubble__text-count').forEach((node) => {
        node.textContent = count;
      });
      document.querySelectorAll('a[href$="/cart"], a[href="/cart"]').forEach((link) => {
        link.setAttribute('aria-label', `${this.dataset.headerTitle || 'Cart'}, ${count} items`);
      });
    }

    async changeLine(line, quantity, refresh = true) {
      if (!line) return;
      await this.request('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ line, quantity })
      });
      if (refresh) await this.refresh(false);
    }

    async changeLineByKey(id, quantity, refresh = true) {
      await this.request('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id, quantity })
      });
      if (refresh) await this.refresh(false);
    }

    async addVariant(id, quantity = 1, properties = {}, refresh = true) {
      if (!id) return;
      await this.request('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ id: Number(id), quantity, properties })
      });
      if (refresh) await this.refresh(true);
    }

    debouncedQuantity(item, quantity) {
      const key = item.dataset.key || item.dataset.line;
      const display = item.querySelector('[data-vcd-quantity]');
      if (display) display.textContent = quantity;
      window.clearTimeout(this.quantityTimers.get(key));
      this.quantityTimers.set(key, window.setTimeout(() => {
        this.setBusy(true);
        this.changeLine(Number(item.dataset.line), quantity)
          .finally(() => this.setBusy(false));
      }, 280));
    }

    async onClick(event) {
      if (this.dragState.moved && event.target.closest('[data-upsell-rail]')) {
        event.preventDefault();
        event.stopPropagation();
        this.dragState.moved = false;
        return;
      }

      if (event.target.closest('[data-vcd-close]')) {
        this.close();
        return;
      }

      const carButton = event.target.closest('[data-car-prev], [data-car-next]');
      if (carButton) {
        const rail = this.querySelector('[data-upsell-rail]');
        if (rail) {
          const card = rail.querySelector('.vcd__upcard');
          const gap = parseFloat(getComputedStyle(rail).columnGap) || 14;
          const pitch = card ? card.getBoundingClientRect().width + gap : 144;
          const direction = carButton.hasAttribute('data-car-next') ? 1 : -1;
          rail.scrollBy({ left: direction * pitch, behavior: 'auto' });
        }
        return;
      }

      const qtyButton = event.target.closest('[data-vcd-qty]');
      if (qtyButton) {
        const item = qtyButton.closest('[data-line]');
        const current = Number(item.querySelector('[data-vcd-quantity]')?.textContent || 1);
        this.debouncedQuantity(item, Math.max(0, current + Number(qtyButton.dataset.vcdQty)));
        return;
      }

      const removeButton = event.target.closest('[data-vcd-remove]');
      if (removeButton) {
        const item = removeButton.closest('[data-line]');
        this.setBusy(true);
        this.changeLine(Number(item.dataset.line), 0).finally(() => this.setBusy(false));
        return;
      }

      const addButton = event.target.closest('[data-vcd-add-variant]');
      if (addButton) {
        this.setBusy(true);
        this.addVariant(addButton.dataset.variantId).finally(() => this.setBusy(false));
        return;
      }

      const upsellToggle = event.target.closest('[data-vcd-upsell-toggle]');
      if (upsellToggle) {
        this.toggleUpsellPanel(upsellToggle);
        return;
      }

      const panelClose = event.target.closest('[data-vcd-panel-close]');
      if (panelClose) {
        this.closeUpsellPanel();
        return;
      }

      const swatchOrPill = event.target.closest('[data-vcd-swatch], [data-vcd-pill]');
      if (swatchOrPill) {
        this.selectPanelValue(swatchOrPill);
        return;
      }

      const panelAdd = event.target.closest('[data-vcd-panel-add]');
      if (panelAdd) {
        const variantId = panelAdd.dataset.variantId;
        if (variantId) {
          this.setBusy(true);
          this.addVariant(variantId).finally(() => this.setBusy(false));
        }
        return;
      }

      const priority = event.target.closest('[data-vcd-priority-toggle]');
      if (priority) {
        const active = priority.getAttribute('aria-pressed') === 'true';
        this.setBusy(true);
        if (active) {
          const line = this.findLineByVariant(Number(priority.dataset.variantId));
          if (line) await this.changeLine(line, 0);
        } else {
          await this.addVariant(priority.dataset.variantId);
        }
        this.setBusy(false);
      }
    }

    findLineByVariant(variantId) {
      const item = this.querySelector(`.vcd__item[data-variant-id="${variantId}"]`);
      return item ? Number(item.dataset.line) : null;
    }

    onRailPointerDown(event) {
      const rail = event.target.closest('[data-upsell-rail]');
      if (!rail) return;
      this.dragState.active = true;
      this.dragState.rail = rail;
      this.dragState.moved = false;
      this.dragState.startX = event.pageX;
      this.dragState.startScroll = rail.scrollLeft;
      rail.classList.add('is-dragging');
    }

    onRailPointerMove(event) {
      if (!this.dragState.active || !this.dragState.rail) return;
      const dx = event.pageX - this.dragState.startX;
      if (Math.abs(dx) > 4) this.dragState.moved = true;
      this.dragState.rail.scrollLeft = this.dragState.startScroll - dx;
    }

    onRailPointerUp() {
      if (this.dragState.rail) this.dragState.rail.classList.remove('is-dragging');
      this.dragState.active = false;
      this.dragState.rail = null;
    }

    isColorOption(name) {
      return /colou?r/i.test(name || '');
    }

    toggleUpsellPanel(button) {
      const panel = this.querySelector('[data-vcd-variant-panel]');
      if (!panel) return;
      const productId = button.dataset.productId;

      if (panel.dataset.productId === productId && !panel.hidden) {
        this.closeUpsellPanel();
        return;
      }

      const card = button.closest('.vcd__upcard');
      const script = card ? card.querySelector('[data-vcd-product-data]') : null;
      if (!script) return;

      let product;
      try {
        product = JSON.parse(script.textContent);
      } catch (error) {
        return;
      }

      this.querySelectorAll('[data-vcd-upsell-toggle]').forEach((btn) => {
        btn.textContent = btn.dataset.defaultLabel || btn.textContent;
      });
      button.textContent = 'Close';

      panel.hidden = false;
      panel.dataset.productId = productId;

      const currentVariantId = Number(card.dataset.variantId);
      const currentVariant = product.variants.find((variant) => variant.id === currentVariantId);
      const selectedValues = {};
      product.options.forEach((option, index) => {
        selectedValues[index] = (currentVariant && currentVariant[`option${index + 1}`]) || option.values[0];
      });

      this.renderUpsellPanel(product, selectedValues);
      panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    closeUpsellPanel() {
      const panel = this.querySelector('[data-vcd-variant-panel]');
      if (!panel) return;
      panel.hidden = true;
      panel.innerHTML = '';
      delete panel.dataset.productId;
      delete panel.dataset.product;
      delete panel.dataset.selected;
      this.querySelectorAll('[data-vcd-upsell-toggle]').forEach((btn) => {
        btn.textContent = btn.dataset.defaultLabel || btn.textContent;
      });
    }

    renderUpsellPanel(product, selectedValues) {
      const panel = this.querySelector('[data-vcd-variant-panel]');
      if (!panel) return;

      const match = product.variants.find((variant) =>
        product.options.every((option, index) => variant[`option${index + 1}`] === selectedValues[index])
      );

      const groups = product.options.map((option, index) => {
        const isColor = this.isColorOption(option.name);
        const items = option.values.map((value) => {
          const active = selectedValues[index] === value;
          const availableAnywhere = product.variants.some((variant) =>
            variant[`option${index + 1}`] === value && variant.available
          );
          if (isColor) {
            const sample = product.variants.find((variant) => variant[`option${index + 1}`] === value);
            const image = sample ? sample.image : '';
            return `<button type="button" class="vcd__swatch${active ? ' is-active' : ''}" data-vcd-swatch data-option-index="${index}" data-value="${this.escape(value)}" style="background-image:url('${this.escape(image)}')" ${availableAnywhere ? '' : 'disabled'} aria-label="${this.escape(value)}" aria-pressed="${active}"></button>`;
          }
          return `<button type="button" class="vcd__pill${active ? ' is-active' : ''}" data-vcd-pill data-option-index="${index}" data-value="${this.escape(value)}" ${availableAnywhere ? '' : 'disabled'} aria-pressed="${active}">${this.escape(value)}</button>`;
        }).join('');

        return `
          <div class="vcd__variant-panel-group">
            <p class="vcd__variant-panel-label">${this.escape(option.name)}</p>
            <div class="${isColor ? 'vcd__variant-swatches' : 'vcd__variant-pills'}">${items}</div>
          </div>
        `;
      }).join('');

      const colorIndex = product.options.findIndex((option) => this.isColorOption(option.name));
      const subtitleValue = colorIndex > -1 ? selectedValues[colorIndex] : Object.values(selectedValues)[0];
      const priceText = match ? this.formatMoney(Math.round(parseFloat(match.price)), true) : '';
      const addLabel = match ? (match.available ? 'Add to bag' : 'Sold out') : 'Unavailable';

      panel.innerHTML = `
        <div class="vcd__variant-panel-head">
          <div>
            <h4 class="vcd__variant-panel-title">${this.escape(product.title)}</h4>
            <p class="vcd__variant-panel-subtitle">${this.escape(subtitleValue || '')}${priceText ? ' — ' + priceText : ''}</p>
          </div>
          <button type="button" class="vcd__variant-panel-close" data-vcd-panel-close aria-label="Close">&times;</button>
        </div>
        ${groups}
        <button type="button" class="vcd__variant-panel-add" data-vcd-panel-add data-variant-id="${match ? match.id : ''}" ${match && match.available ? '' : 'disabled'}>${addLabel}</button>
      `;

      panel.dataset.product = JSON.stringify(product);
      panel.dataset.selected = JSON.stringify(selectedValues);
    }

    selectPanelValue(control) {
      const panel = this.querySelector('[data-vcd-variant-panel]');
      if (!panel) return;
      let product;
      let selectedValues;
      try {
        product = JSON.parse(panel.dataset.product || 'null');
        selectedValues = JSON.parse(panel.dataset.selected || '{}');
      } catch (error) {
        return;
      }
      if (!product || control.disabled) return;

      selectedValues[control.dataset.optionIndex] = control.dataset.value;
      this.renderUpsellPanel(product, selectedValues);
    }

    onDocumentClick(event) {
      const opener = event.target.closest('[data-vcd-open], a[href$="/cart"], a[href="/cart"]');
      if (!opener || this.contains(opener)) return;
      event.preventDefault();
      this.open();
    }

    async onSubmit(event) {
      const form = event.target;
      if (!form || !form.matches('form[action*="/cart/add"]')) return;
      if (this.dataset.autoOpenAtc !== 'true') return;

      event.preventDefault();
      this.setBusy(true);
      try {
        const formData = new FormData(form);
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          body: formData
        });
        if (!response.ok) throw new Error('Add to cart failed');
        await this.refresh(true);
      } finally {
        this.setBusy(false);
      }
    }

    onKeydown(event) {
      if (!this.openState) return;
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = this.querySelectorAll('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])');
      const nodes = Array.from(focusable).filter((node) => node.offsetParent !== null);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    formatMoney(cents, withCurrency) {
      const ZERO_DECIMAL = ['JPY', 'KRW', 'VND', 'CLP', 'ISK', 'HUF'];
      const THREE_DECIMAL = ['KWD', 'BHD', 'OMR', 'IQD', 'JOD', 'LYD', 'TND'];
      const storeCurrency = this.dataset.currency || 'OMR';
      const value = Number(cents || 0) / 100;

      // Mirror the site-wide cosmetic currency converter (assets/cosmetic-currency.js):
      // same sessionStorage/localStorage keys, same USD cross-rate, same round-to-10.
      // Kept as a self-contained copy since this section loads independently and the
      // theme has no shared JS module system.
      const displayCurrency = this.getDisplayCurrency();
      if (displayCurrency && displayCurrency !== storeCurrency) {
        const rate = this.getDisplayRate(storeCurrency, displayCurrency);
        if (rate) {
          const converted = Math.round((value * rate) / 10) * 10;
          return `${converted.toLocaleString('en-US')} ${displayCurrency}`;
        }
      }

      const digits = ZERO_DECIMAL.includes(storeCurrency) ? 0 : THREE_DECIMAL.includes(storeCurrency) ? 3 : 2;
      const format = (withCurrency ? this.dataset.moneyWithCurrencyFormat : this.dataset.moneyFormat) || '{{amount}}';
      const usesCommaDecimal = /amount_with_comma_separator|amount_no_decimals_with_comma_separator/.test(format);
      const formatted = value.toLocaleString(usesCommaDecimal ? 'de-DE' : 'en-US', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits
      });
      const withAmount = format.replace(
        /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}|\{\{\s*amount_with_comma_separator\s*\}\}|\{\{\s*amount_no_decimals\s*\}\}|\{\{\s*amount\s*\}\}/,
        formatted
      );
      return withAmount.replace(/<[^>]*>/g, '').trim();
    }

    getDisplayCurrency() {
      try {
        return sessionStorage.getItem('cosmeticCurrency');
      } catch (error) {
        return null;
      }
    }

    getDisplayRate(storeCurrency, targetCurrency) {
      try {
        const cached = JSON.parse(localStorage.getItem('cosmeticCurrencyRates') || 'null');
        if (!cached || !cached.rates) return null;
        const usdToStore = cached.rates[storeCurrency];
        const usdToTarget = cached.rates[targetCurrency];
        if (!usdToStore || !usdToTarget) return null;
        return usdToTarget / usdToStore;
      } catch (error) {
        return null;
      }
    }

    escape(value) {
      return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  }

  if (!customElements.get('verani-cart-drawer')) {
    customElements.define('verani-cart-drawer', VeraniCartDrawer);
  }
})();
